import { Button, TextField, FormControl, InputLabel, InputAdornment } from "@mui/material"
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { useCallback, useEffect, useState } from "react";
import AddProductOptions from "./AddProductOptions";
import { uid } from "uid";
import DeleteIcon from '@mui/icons-material/Delete';
import FormHelperText from '@mui/material/FormHelperText';
import { setProduct } from "../utils/database";
import UploadIcon from '@mui/icons-material/Upload';
import { USER_PLANS, createProduct } from "../utils/structures";
import OutlinedInput from '@mui/material/OutlinedInput';
import Styles from './styles/AddProduct.module.css'
import { useRef } from "react";
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getImage, uploadFileURL } from "../utils/filesystem";
import { isValidImg } from "../utils/files";
import EasyCrop from "./EasyCrop";
import Confirmation from "./Confirmation";
import AddProductCategories from "./AddProductCategories";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import Dialog from '@mui/material/Dialog';
import { capitalizeText } from "../utils/strings";
import Helper from './Helper'
import EasySnackBar from "./EasySnackBar";
import { isValidWebp } from "../utils/files";

export default function AddProduct(props) {
    const IMAGE_QUALITY = 0.8

    const productImageRef = useRef()

    const [showConfirmation, setShowConfirmation] = useState(false)

    const [featured, setFeatured] = useState(false)
    const [name, setName] = useState("")
    const [caption, setCaption] = useState("")
    const [price, setPrice] = useState(0)
    const [productCategory, setProductCategory] = useState("")
    const [productOptions, setProductOptions] = useState([])
    const [calories, setCalories] = useState(0)

    const [productImg, setProductImg] = useState(null)
    const [loadedImg, setLoadedImg] = useState(null)

    const [productModified, setProductModified] = useState(false)

    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    useEffect(() => {
        if (props.initialProduct) {
            setName(props.initialProduct[1].name)
            setProductCategory(props.initialProduct[1].category)

            setCaption(props.initialProduct[1].caption ?
                props.initialProduct[1].caption : "")
            setPrice(props.initialProduct[1].price ?
                props.initialProduct[1].price : 0)
            setProductOptions(props.initialProduct[1].options ?
                props.initialProduct[1].options : [])
            setFeatured(props.initialProduct[1].featured)
            setCalories(props.initialProduct[1].calories)

            getProductImg()
        }
    }, [props.initialProduct])

    useEffect(() => {

        if (props.initialProduct) {
            if (props.initialProduct[1].name !== name
                || props.initialProduct[1].caption !== caption
                || props.initialProduct[1].price !== price
                || (props.initialProduct[1].options && props.initialProduct[1].options !== productOptions)
                || (!props.initialProduct[1].options && productOptions.length > 0)
                || props.initialProduct[1].category !== productCategory
                || props.initialProduct[1].featured !== featured
                || props.initialProduct[1].calories !== calories) {

                props.setPreventNavigation(true)
                setProductModified(true)
            } else {
                props.setPreventNavigation(false)
                setProductModified(false)
            }

        } else {
            if (name !== '' || caption !== '' || price !== 0 || productOptions.length > 0 || productCategory !== '') {
                props.setPreventNavigation(true)
                setProductModified(true)

            } else {
                props.setPreventNavigation(false)
                setProductModified(false)
            }
        }

    }, [name, caption, price, productOptions, productCategory, featured, calories])

    const userImgLoad = img => {
        if (!isValidImg(img)) {
            setErrorMessage('Imagen invalida, intenta con otra')
        } else {
            setLoadedImg(URL.createObjectURL(img))
        }
    }

    const getProductImg = async () => {
        const reqImg = await getImage(props.userAuth.uid + "/" + props.initialProduct[0] + '/01.jpg')

        if (isValidWebp(reqImg)) {
            setProductImg(URL.createObjectURL(reqImg))
        }
    }

    const resetStates = useCallback(() => {
        setName('')
        setCaption('')
        setPrice(0)
        setProductCategory('')
        setProductOptions([])
        setProductImg(null)
        setLoadedImg(null)
        setProductModified(false)
        setFeatured(false)
        setCalories(0)

        props.setInitialProduct(undefined)
        props.setPreventNavigation(false)
        props.setOpenDialog(false)
    })

    const modifyProductImg = image => {
        props.setPreventNavigation(true)
        setProductModified(true)
        setProductImg(image)
    }

    const addProduct = async () => {

        if (props.userMenu.plan.type === USER_PLANS.free
            && props.userMenu.products
            && Object.values(props.userMenu.products).length === 10) {
            setErrorMessage('Alcanzaste el limite de productos del plan gratuito, mejora al plan PRO')

        } else if (name === '') {
            setErrorMessage('El producto necesita un nombre')

        } else if (productCategory === '') {
            setErrorMessage('El producto necesita una categoria')

        } else if (productOptions.reduce((prev, option) => prev || !option.subOptions || option.subOptions.length === 0, false)) {
            setErrorMessage('Todas las versiones de tu producto deben tener al menos una opción para elegir')

        } else {
            const productID = props.initialProduct ? props.initialProduct[0] : uid()

            uploadFileURL(productImg, props.userAuth.uid + "/" + productID + '/01.jpg')

            setProduct(props.userAuth, props.userData.menuid, productID, createProduct(
                capitalizeText(name),
                caption.charAt(0).toUpperCase() + caption.slice(1),
                productCategory,
                parseFloat(price),
                0,
                productOptions,
                featured,
                parseInt(calories === '' ? 0 : calories)
            ))

            setSuccessMessage('Producto creado')
            props.updateUserMenu()
            resetStates()
        }
    }

    const removeProduct = useCallback(() => {
        setProduct(props.userAuth, props.userData.menuid, props.initialProduct[0], null)
        props.updateUserMenu()
    }, [props.userAuth, props.userData, props.initialProduct])

    const removeProductImage = () => {
        setLoadedImg(null)
        setProductImg(null)
        props.setPreventNavigation(true)
        setProductModified(true)
    }

    return (
        <>
            <Dialog open={props.openDialog} onClose={resetStates.bind(this)} fullScreen>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={resetStates.bind(this)}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Administrar producto
                        </Typography>

                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Button
                                variant="contained"
                                onClick={productModified || props.initialProduct ? setShowConfirmation.bind(this, true) : props.setOpenDialog.bind(this, false)}
                                color="error"
                                sx={{ width: '175px', m: 1 }}
                            >
                                {props.initialProduct ? 'Eliminar' : 'Descartar'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={addProduct.bind(this)}
                                color="success"
                                sx={{ width: '175px', m: 1 }}
                                disabled={productModified ? false : true}
                            >
                                Guardar cambios
                            </Button>
                        </Stack>
                    </Toolbar>
                </AppBar>

                <DialogContent sx={{ paddingBottom: '75px' }}>
                    {loadedImg ?
                        <EasyCrop
                            loadedImg={loadedImg}
                            setLoadedImg={setLoadedImg}
                            setImage={modifyProductImg}
                            aspectRatio={2 / 1}
                            quality={IMAGE_QUALITY}
                        /> :

                        <Stack spacing={3} className={Styles.container}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-evenly"
                                flexWrap='wrap'
                                gap='15px'
                            >

                                <label
                                    className={Styles.productImage}
                                    style={productImg ? { backgroundImage: "url(" + productImg + ")" } : null}
                                >
                                    <input type="file" accept='image/*'
                                        onChange={e => {
                                            userImgLoad(e.target.files[0])
                                        }}
                                        ref={productImageRef}
                                    />
                                </label>

                                <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>

                                    {!productImg && !loadedImg ? null :
                                        <Button variant="outlined" color="error"
                                            onClick={() => removeProductImage()}
                                        >
                                            Eliminar imagen
                                            <DeleteIcon />
                                        </Button>}

                                    <Button variant="outlined"
                                        onClick={() => productImageRef ? productImageRef.current.click() : null}
                                    >
                                        Subir una nueva imagen
                                        <UploadIcon />
                                    </Button>
                                </Stack>

                            </Stack>

                            <Divider />

                            <Stack direction='row'>
                                <FormControl variant="outlined"
                                    sx={{ width: '100%' }}
                                    required
                                >
                                    <InputLabel htmlFor="outlined-adornment-password">Nombre del producto</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-password"
                                        required
                                        onChange={e => e.target.value.length <= 100 ? setName(e.target.value) : null}
                                        value={name}
                                        endAdornment={
                                            <InputAdornment position="end">

                                                <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        if (props.userMenu.plan.type === USER_PLANS.free) {
                                                            setErrorMessage('Para tener productos destacados necesitas conseguir el plan PRO')
                                                        } else {
                                                            setFeatured(!featured)
                                                        }
                                                    }
                                                    }
                                                >
                                                    {featured ?
                                                        <StarIcon fontSize="large" sx={{ color: 'rgb(255, 191, 0)' }} /> :
                                                        <StarBorderIcon fontSize="large" sx={{ color: 'rgb(255, 191, 0)' }} />}
                                                </IconButton>

                                            </InputAdornment>
                                        }
                                        label="Nombre del producto"
                                    />
                                    <FormHelperText>Maximo 100 caracteres</FormHelperText>
                                </FormControl>

                                <Helper
                                    text='Los productos que destaques se mostrarán primeros en tu menú'
                                    style={{ marginBottom: '20px' }}
                                />
                            </Stack>

                            <Stack direction='row'>
                                <TextField
                                    id="filled-textarea"
                                    variant="outlined"
                                    helperText="Maximo 1500 caracteres"
                                    label="Descripcion"
                                    multiline
                                    fullWidth
                                    value={caption}
                                    onChange={e => e.target.value.length <= 1500 ? setCaption(e.target.value) : null}
                                />
                                <Helper
                                    text='Usa la descripción para explicarles a tus clientes en que consiste el producto'
                                    style={{ marginBottom: '20px' }}
                                />
                            </Stack>

                            <Stack direction='row' spacing={3}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="outlined-adornment-amount" required>Precio</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-amount"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Precio"
                                        value={price}
                                        required
                                        onChange={e => {
                                            const inputVal = e.target.value.replace(',', '.');
                                            const regex = /^(\d*)([,.]\d{0,2})?$/;

                                            if (inputVal === "") {
                                                setPrice(0)

                                            } else if (regex.test(inputVal)) {
                                                setPrice(inputVal.at(0) === '0' && inputVal.length > 0 ? inputVal.slice(1,) : inputVal)
                                            }
                                        }}
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel>Calorias</InputLabel>
                                    <OutlinedInput
                                        endAdornment={<InputAdornment position="end">Kcal</InputAdornment>}
                                        label="Calorias"
                                        value={calories}
                                        required
                                        onChange={e => {
                                            const regex = /^[0-9]*$/
                                            const inputVal = e.target.value

                                            if (regex.test(inputVal)) {
                                                const val = inputVal.at(0) === '0' && inputVal.length > 1 ? inputVal.slice(1,) : inputVal
                                                setCalories(val)
                                            }
                                        }}
                                    />
                                    <FormHelperText>Dejar en 0 para omitir esta informacion</FormHelperText>
                                </FormControl>
                            </Stack>

                            <Divider />

                            <AddProductCategories
                                productCategory={productCategory}
                                setProductCategory={setProductCategory}
                                allCategories={props.userMenu ? props.userMenu.categories : null}
                                userAuth={props.userAuth}
                                menuID={props.userMenu ? props.userData.menuid : null}
                                updateUserMenu={props.updateUserMenu}
                            />

                            <Divider />

                            <AddProductOptions
                                userMenu={props.userMenu}
                                productOptions={productOptions}
                                setProductOptions={setProductOptions}
                            />
                        </Stack>
                    }

                </DialogContent>
            </Dialog >

            {props.initialProduct ?
                <Confirmation
                    action={() => { removeProduct(); setSuccessMessage('Producto eliminado'); resetStates() }}
                    open={showConfirmation}
                    setOpen={setShowConfirmation}
                    title={"Estas a punto de eliminar tu producto"}
                    description={"Estas seguro de que quieres eliminar tu producto? Ya no se mostrará en tu menú ni podrás volver a editarlo"}
                    actionMsg={"Eliminar"}
                    rejectMsg={"Volver"}
                /> :
                <Confirmation
                    action={resetStates}
                    open={showConfirmation}
                    setOpen={setShowConfirmation}
                    title={"Estás seguro de que quieres descartar los cambios?"}
                    description={""}
                    actionMsg={"Descartar"}
                    rejectMsg={"Volver"}
                />}

            <EasySnackBar
                severity='error'
                key='1'
                message={errorMessage}
                setMessage={setErrorMessage}
            />

            <EasySnackBar
                severity='success'
                key='2'
                message={successMessage}
                setMessage={setSuccessMessage}
            />
        </>
    )
}